// The engineering-paper grid every sheet is laid on: a line every 16 px, a heavier rule every
// 80 px. The arithmetic here is pure; hooks/useGridSnap.ts measures the page and applies it, and
// components/BlueprintBackdrop.tsx draws the grid from the same origin, so a sheet's edges and
// the lines it sits on are one fact rather than two.

export const GRID_STEP = 16;
export const GRID_MAJOR = 80;

/** Below this a position already counts as on the line (sub-pixel layout). */
const TOLERANCE = 0.5;

export function mod(value: number, step: number): number {
  return ((value % step) + step) % step;
}

/** How far to move `pos` forward so it lands on a grid line (0 when it already does). */
export function pushToLine(pos: number, origin: number, step = GRID_STEP): number {
  const r = mod(pos - origin, step);
  return r < TOLERANCE || step - r < TOLERANCE ? 0 : step - r;
}

/** How far to pull `pos` back so it lands on a grid line (0 when it already does). */
export function trimToLine(pos: number, origin: number, step = GRID_STEP): number {
  const r = mod(pos - origin, step);
  return r < TOLERANCE || step - r < TOLERANCE ? 0 : r;
}

/** The smallest whole number of grid cells that holds `size`. */
export function growToStep(size: number, step = GRID_STEP): number {
  return Math.max(step, Math.ceil((size - TOLERANCE) / step) * step);
}

/** The nearest grid line to `pos`. */
export function nearestLine(pos: number, origin: number, step = GRID_STEP): number {
  return origin + Math.round((pos - origin) / step) * step;
}

/** Whether the line at `pos` is a major rule (every fifth line from the origin). */
export function isMajorLine(pos: number, origin: number, step = GRID_STEP, major = GRID_MAJOR): boolean {
  return mod(Math.round((pos - origin) / step), major / step) === 0;
}

// The origin of the grid in the scrolling content's coordinates (0..15 on each axis). The snapper
// sets it from the page's leftmost / topmost sheet; the backdrop reads it every frame.
let origin = { x: 0, y: 0 };

export function getGridOrigin(): { x: number; y: number } {
  return origin;
}

export function setGridOrigin(x: number, y: number): void {
  origin = { x: mod(x, GRID_STEP), y: mod(y, GRID_STEP) };
}
